import { DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Card } from '@components/card';
import { AVATAR_GROUPS } from '@components/design-avatar/avatars';
import { useSortableDefaults } from '@components/drag-and-drop/defaults';
import { createForm, useCreateFormStore } from '@components/form';
import { IconDragHover } from '@components/icon-hover-container/icon-hover-container';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { InfoTooltip } from '@components/info-tooltip';
import { Menu } from '@components/menu';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useCreateAIAgent, useHiddenModels, useUpdateAIAgent } from 'api/ai-agents/hooks';
import { AIAgent } from 'api/ai-agents/requests';
import { useUploadFile } from 'api/files/hooks';
import { AIModel, AIReasoningEffort } from 'api/types';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';

import {
  DEFAULT_AGENT_OUTPUT,
  DEFAULT_MODEL,
  getReasoningEffortConfig,
  normalizeReasoningEffortForModel,
} from './ai-agent-config';
import { useModelSelectorModal } from './modal-selector-modal';
import { AIModelSelector } from './model-selector';
import { useOutputModal } from './output-modal';
import { Property } from './shared/output-list';
import {
  AIAgentConfigurationFields,
  AIAgentKnowledgeFilesField,
  AIAgentOutputFields,
  AIAgentToolsFields,
  fileUrlsToUploadedFiles,
} from './shared/sidebar-form-sections';

type AIAgentFormData = Omit<AIAgent, 'files'> & {
  files: {
    name: string;
    url: string;
  }[];
};

const Form = createForm<AIAgentFormData>();

type OutputProps = {
  items?: Array<Property>;
  handleOutputModal?: (index?: number) => void;
  handleDeleteOutput?: (index: number) => void;
  setValue: (value: Array<Property>) => void;
};

export const Output = ({
  items: propItems = [],
  handleDeleteOutput,
  handleOutputModal,
  setValue,
}: OutputProps) => {
  const getId = (item: Property) => item.name;
  const {
    items,
    itemIds,
    activeItem,
    handleDragEnd,
    handleDragStart,
    handleDragOver,
    strategy,
    ...sortingDefaults
  } = useSortableDefaults({
    items: propItems,
    getId,
    onDragEnd: (newOrder) => {
      setValue(newOrder.map((name) => propItems.find((item) => item.name === name)!));
    },
  });

  return (
    <DndContext
      {...sortingDefaults}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
    >
      <SortableContext items={itemIds} strategy={strategy}>
        {items.map((item, index) => (
          <OutputItem
            key={item.name}
            item={item}
            index={index}
            handleDeleteOutput={handleDeleteOutput}
            handleOutputModal={handleOutputModal}
          />
        ))}
      </SortableContext>

      <DragOverlay>{activeItem && <OutputItem item={activeItem} index={0} />}</DragOverlay>
    </DndContext>
  );
};

const OutputItem = ({
  item,
  index,
  handleOutputModal,
  handleDeleteOutput,
}: {
  item: Property;
  index: number;
  handleOutputModal?: (index?: number) => void;
  handleDeleteOutput?: (index: number) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.name,
  });

  return (
    <div ref={setNodeRef}>
      <Card
        spaceX={3}
        spaceY={1}
        interactive
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          ...(isDragging && {
            opacity: 0,
          }),
        }}
      >
        <Stack orientation="horizontal" justify="space-between">
          <Stack orientation="horizontal" space={2} align="center" style={{ overflow: 'hidden' }}>
            <IconDragHover
              icon={<AttributeIcon type={item.type} />}
              {...attributes}
              {...listeners}
            />
            <Text text={item.label} ellipsisWithTooltip />
            <InfoTooltip
              tooltip={
                item.instructions.length > 300
                  ? `${item.instructions.slice(0, 300)}...\n\nEdit to view full instructions`
                  : item.instructions
              }
            />
          </Stack>
          <Menu
            buttonProps={{
              variant: 'ghost',
              iconRight: <IconMoreVertical />,
              size: 'sm',
            }}
          >
            <Menu.Item
              label={t('core.edit')}
              icon={<IconEdit />}
              onSelect={() => handleOutputModal?.(index)}
            />
            <Menu.Item
              label={t('core.delete')}
              variant="danger"
              onSelect={() => handleDeleteOutput?.(index)}
              icon={<IconTrash />}
            />
          </Menu>
        </Stack>
      </Card>
    </div>
  );
};

export const useAgentDetailsSidebar = () =>
  useSidebar(AgentDetailsSidebar, { variant: 'CONTENT_HEAVY', closeOnClickOutside: true });

const AgentDetailsSidebar = (props: AIAgent) => {
  const { t } = useScopedTranslate(locale);

  const isUpdate = Boolean(props.shortId);

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={isUpdate ? t('editAgent') : t('createAgent')}
        icon={IconAiAgent}
        iconColor="blue100"
      />
      <Details {...props} />
    </Sidebar>
  );
};

const Details = (props: AIAgent) => {
  const { t } = useScopedTranslate(locale);

  const {
    shortId,
    output: propOutput,
    model: propModel,
    picture,
    name,
    description,
    systemInstructions,
    reasoningEffort,
    files,
    useWebSearch,
    useFileSearch,
  } = props;

  const isUpdate = Boolean(shortId);
  const createMutation = useCreateAIAgent();
  const updateMutation = useUpdateAIAgent();
  const formStore = useCreateFormStore();
  const showOutputModal = useOutputModal();
  const defaultOutput = isUpdate
    ? propOutput ?? []
    : propOutput && propOutput.length > 0
    ? propOutput
    : [DEFAULT_AGENT_OUTPUT];
  const currentOutput = formStore.useFieldValue('output') as Property[] | undefined;
  const output = currentOutput ?? defaultOutput;
  const currentSystemInstructions = formStore.useFieldValue('systemInstructions') as
    | string
    | undefined;
  const resolvedSystemInstructions = currentSystemInstructions ?? systemInstructions ?? '';
  const isGenerateOutputDisabled = resolvedSystemInstructions.trim().length === 0;
  const model = formStore.useFieldValue('model') as AIModel;
  const { data: hiddenModelsData } = useHiddenModels();
  const hiddenModels = hiddenModelsData?.models ?? [];
  const resolvedModel = model ?? propModel ?? DEFAULT_MODEL;
  const showModelSelectorModal = useModelSelectorModal();
  const reasoningEffortConfig = getReasoningEffortConfig(resolvedModel);

  const handleOutputModal = (index?: number) => {
    showOutputModal({
      setValue: (value) => formStore.actions.setValue('output', value),
      getValue: () => formStore.getState().values['output'] as Property[],
      currentIndex: index,
    });
  };

  const handleDeleteOutput = (index: number) => {
    const state = formStore.getState();
    const currentOutput = (state.values['output'] ?? []) as Property[];
    formStore.actions.setValue(
      'output',
      currentOutput.filter((_, i) => i !== index)
    );
  };

  const handleSelectModel = (nextModel: AIModel) => {
    formStore.actions.setValue('model', nextModel);

    const currentReasoningEffort =
      (formStore.getState().values['reasoningEffort'] as AIReasoningEffort | undefined) ??
      reasoningEffort ??
      null;
    const normalizedReasoningEffort = normalizeReasoningEffortForModel(
      nextModel,
      currentReasoningEffort
    );

    if (normalizedReasoningEffort) {
      formStore.actions.setValue('reasoningEffort', normalizedReasoningEffort);
    }
  };

  const handleOpenModelSelector = () => {
    showModelSelectorModal({
      currentModel: resolvedModel,
      hiddenModels,
      onSelectModel: handleSelectModel,
    });
  };

  const onSubmit = async (formValues: AIAgentFormData) => {
    const mutation = isUpdate ? updateMutation : createMutation;
    const formFiles = formValues.files ?? [];
    const data = {
      ...formValues,
      shortId,
      output: formValues.output,
      files: formFiles.map((file) => file.url),
    };
    try {
      await mutation.mutateAsync(data);
      hideSidebar();
    } catch (e) {
      // Handle error?
    }
  };
  const hasOutputError = formStore.useFormErrors().output;

  const uploadMutation = useUploadFile('ai-agent-templates', true);

  return (
    <Form onSubmit={onSubmit} store={formStore}>
      <Sidebar.Content>
        <Stack space={6}>
          <Sidebar.SplitContainer label={t('details')}>
            <Stack space={4}>
              <Form.DesignAvatarChooserField
                id="picture"
                label={t('agentImage')}
                avatars={AVATAR_GROUPS.robots}
                onSelect={uploadMutation.mutateAsync}
                defaultImage={picture}
              />
              <Form.TextField
                id="name"
                label={t('agentName')}
                defaultValue={name}
                validators={['required']}
              />
              <Form.TextField
                id="description"
                label={t('description')}
                defaultValue={description}
                placeholder={t('addDescription')}
              />
            </Stack>
          </Sidebar.SplitContainer>
          <Sidebar.Separator />
          <Sidebar.SplitContainer label={t('configuration')}>
            <AIAgentConfigurationFields
              Form={Form}
              defaultModel={propModel || DEFAULT_MODEL}
              resolvedModel={resolvedModel}
              defaultSystemInstructions={systemInstructions}
              defaultReasoningEffort={reasoningEffort}
              reasoningEffortConfig={reasoningEffortConfig}
              isTemplate
              onOpenModelSelector={handleOpenModelSelector}
              renderModelSelector={({ model, onOpen }) => (
                <AIModelSelector model={model as AIModel} onOpen={onOpen} />
              )}
            />
          </Sidebar.SplitContainer>
          <Sidebar.Separator />
          <Sidebar.SplitContainer label={t('knowledge')}>
            <AIAgentKnowledgeFilesField
              Form={Form}
              isTemplate
              fieldId="files"
              helperText={t('knowledgeInstructions')}
              defaultFiles={fileUrlsToUploadedFiles(files)}
              onSelect={uploadMutation.mutateAsync}
            />
          </Sidebar.SplitContainer>
          <Sidebar.Separator />
          <Sidebar.SplitContainer label={t('tools')}>
            <AIAgentToolsFields
              Form={Form}
              defaultUseWebSearch={useWebSearch}
              defaultUseFileSearch={useFileSearch}
            />
          </Sidebar.SplitContainer>
          <Sidebar.Separator />
          <Sidebar.SplitContainer label={t('output')}>
            <AIAgentOutputFields
              Form={Form}
              output={output}
              defaultOutput={defaultOutput}
              systemInstructions={resolvedSystemInstructions}
              setOutput={(value) => formStore.actions.setValue('output', value)}
              handleOutputModal={handleOutputModal}
              handleDeleteOutput={handleDeleteOutput}
              hasOutputError={hasOutputError}
              isGenerateOutputDisabled={isGenerateOutputDisabled}
            />
          </Sidebar.SplitContainer>
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        <Form.Actions>
          <Form.Reset text={t('core.cancel')} onClick={hideSidebar} />
          <Form.Submit text={t('core.save')} shouldDisableWhenNotDirty />
        </Form.Actions>
      </Sidebar.Footer>
    </Form>
  );
};

const locale = {
  en: {
    createAgent: 'Create AI Agent template',
    editAgent: 'Edit AI Agent template',
    agentImage: 'Agent image',
    agentName: 'Agent name',
    description: 'Description',
    addDescription: 'Add description',
    details: 'Details',
    systemInstructions: 'System instructions',
    systemInstructionsPlaceholder: 'You are a helpful assistant...',
    configuration: 'Configuration',
    knowledge: 'Knowledge',
    knowledgeInstructions:
      'Configure your agent with information relevant to the context it will be used for',
    output: 'Output',
    tools: 'Tools',
  },
};
